import { useEffect, useRef, type KeyboardEvent } from 'react'

import { Carousel } from '@/components/ui/Carousel'
import { CreatePoolCard, PoolCard } from '@/features/pools/components/PoolCard'
import type { PoolCardModel } from '@/features/scheduler/poolCards'

interface PoolCarouselProps {
  cards: readonly PoolCardModel[]
  selectedSlug: string | null
  onSelect: (slug: string) => void
  onCreate: () => void
}

/**
 * Os pools do dono em abas roláveis, em cima do detalhe do aberto. ←/→,
 * Home e End trocam de pool (trocar é barato: o detalhe já está em cache), e
 * o aberto rola para a vista ao mudar.
 */
export function PoolCarousel({ cards, selectedSlug, onSelect, onCreate }: PoolCarouselProps) {
  const tabs = useRef(new Map<string, HTMLButtonElement>())
  const scrolled = useRef(false)

  useEffect(() => {
    if (!selectedSlug) return
    tabs.current.get(selectedSlug)?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
      behavior: scrolled.current ? 'smooth' : 'auto',
    })
    scrolled.current = true
  }, [selectedSlug])

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = cards.length - 1
    const target =
      event.key === 'ArrowRight'
        ? Math.min(index + 1, last)
        : event.key === 'ArrowLeft'
          ? Math.max(index - 1, 0)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null
    if (target === null) return
    event.preventDefault()
    const slug = cards[target]?.slug
    if (!slug) return
    onSelect(slug)
    tabs.current.get(slug)?.focus()
  }

  return (
    <Carousel
      itemWidth={220}
      gap={12}
      trackRole="tablist"
      trackLabel="Pools"
      hideNavOnTouch
    >
      {cards.map((card, i) => (
        <PoolCard
          key={card.slug}
          ref={(el) => {
            if (el) tabs.current.set(card.slug, el)
            else tabs.current.delete(card.slug)
          }}
          card={card}
          selected={card.slug === selectedSlug}
          onSelect={() => {
            onSelect(card.slug)
          }}
          onKeyDown={onKeyDown(i)}
        />
      ))}
      <CreatePoolCard onClick={onCreate} />
    </Carousel>
  )
}
